import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import formatPayrollAmount from './teacherSettlementPresentation';
import { displayDate } from '../common/datePresentation';
import { groupSettlementLessons, settlementLessonSchedule, settlementLessonTypeLabel, shortParticipantLabel } from './teacherSettlementGroups';
import TeacherSettlementStatus from './TeacherSettlementStatus';

function Participants({ lesson, t }) {
  if (!lesson.rosterKnown) return <span>{t('unknownLesson')}</span>;
  return (
    <span>{(lesson.participants || []).map((student, index) => (
      <React.Fragment key={student.id}>
        {index > 0 && ', '}
        <span title={student.label}>{shortParticipantLabel(student)}
          {student.isGuest && <small> ({t('guest')})</small>}
          {student.isMakeup && <small> ({t('makeup')})</small>}
        </span>
      </React.Fragment>
    ))}
    </span>
  );
}
Participants.propTypes = { lesson: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

export default function TeacherSettlementLessonGroups({ report, expandedGroupKeys, missingOnly, onToggleGroup, attendanceLink, onAttendanceClick, t, language }) {
  const formatLedgerAmount = value => formatPayrollAmount(value, language);
  const groups = groupSettlementLessons(report.lessonView || []);
  const amountLabel = lesson => (lesson.amountCents === null || lesson.amountCents === undefined
    ? t('unrecordedAmount') : formatLedgerAmount(lesson.amountCents / 100));
  return (
    <div className="payroll-groups">
      <div className="payroll-groups-heading" aria-hidden="true"><span /><span>{t('schedule')}</span><span>{t('group')}</span><span>{t('attendance')}</span>
        <span className="payroll-group-total"><span>{t('paid')}</span><span>{t('amount')}</span></span>
      </div>
      {groups.map((group, index) => {
        if (missingOnly && group.missingAttendanceCount === 0) return null;
        const viewKey = JSON.stringify([report.teacherId, group.key]);
        const expanded = expandedGroupKeys.includes(viewKey);
        const id = `teacher-group-${report.teacherId}-${index}`;
        const names = [...new Map(group.lessons.flatMap(lesson => lesson.participants || []).map(student => [student.id, student])).values()];
        const count = group.hasUnknownRoster ? t('unknown') : `${group.minParticipants}${group.minParticipants !== group.maxParticipants ? `–${group.maxParticipants}` : ''}`;
        const label = [t(`weekdays.${group.dayOfWeek || 'unknown'}`), group.startTime, `${group.durationMinutes || '—'} min`,
          settlementLessonTypeLabel(group.classTypeId, t), `${t('students')}: ${count}`].filter(Boolean).join(' · ');
        let groupTone = 'warning';
        let groupStatus = 'needsReview';
        if (group.missingAttendanceCount > 0) {
          groupTone = 'danger';
        } else if (group.lessons.every(lesson => lesson.attendanceState === 'cancelled')) {
          groupTone = 'muted';
          groupStatus = 'allCancelled';
        } else if (group.lessons.every(lesson => lesson.attendanceState === 'checked')) {
          groupTone = 'success';
          groupStatus = 'attendanceChecked';
        } else if (group.lessons.every(lesson => ['checked', 'cancelled'].includes(lesson.attendanceState))) {
          groupTone = 'success';
          groupStatus = 'attendanceComplete';
        }
        return (
          <section className={`payroll-group${expanded ? ' payroll-group--expanded' : ''}`} key={group.key}>
            <button
              type="button"
              className="payroll-group-toggle"
              aria-label={label}
              aria-describedby={`${id}-identity ${id}-status ${id}-total`}
              aria-expanded={expanded}
              aria-controls={id}
              onClick={() => onToggleGroup(viewKey)}
            >
              <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`} aria-hidden="true" />
              <span className="payroll-group-schedule"><strong>{t(`weekdays.${group.dayOfWeek || 'unknown'}`)}</strong>
                <small>{group.startTime || '—'} · {group.durationMinutes || '—'} min</small>
              </span>
              <span id={`${id}-identity`} className="payroll-group-identity"><span className="payroll-type">{settlementLessonTypeLabel(group.classTypeId, t)}</span>
                <span title={names.map(student => student.label).join(', ')}>{names.map(shortParticipantLabel).join(', ') || t('unknownLesson')}</span>
                <small>{t('students')}: {count}</small>
              </span>
              <TeacherSettlementStatus id={`${id}-status`} tone={groupTone} compact>
                {group.missingAttendanceCount > 0 ? t('missingAttendance', { count: group.missingAttendanceCount }) : t(groupStatus)}
              </TeacherSettlementStatus>
              <span id={`${id}-total`} className="payroll-group-total"><span title={t('lessons')}>{group.payoutEligibleCount} / {group.totalLessonsCount}</span>
                <strong>{group.lessons.some(lesson => lesson.amountCents !== null && lesson.amountCents !== undefined)
                  ? formatLedgerAmount(group.amountCents / 100) : t('unrecordedAmount')}
                </strong>
                {group.hasUnrecordedAmount && group.lessons.some(lesson => lesson.amountCents !== null && lesson.amountCents !== undefined) && <small>{t('unrecordedAmount')}</small>}
              </span>
            </button>
            {expanded && (
              <div id={id} className="payroll-group-lessons">
                <table className="payroll-lessons-table">
                  <thead><tr>{['date', 'attendance', 'source', 'amount', 'actions'].map(key => <th key={key} scope="col">{t(key)}</th>)}</tr></thead>
                  <tbody>{group.lessons.filter(lesson => !missingOnly || lesson.attendanceState === 'missing').map(lesson => {
                    const target = attendanceLink(lesson);
                    const schedule = settlementLessonSchedule(lesson);
                    return (
                      <tr
                        id={`teacher-lesson-${report.teacherId}-${lesson.lessonId}`}
                        tabIndex={-1}
                        key={lesson.lessonId}
                        className={`payroll-lesson payroll-lesson--${lesson.attendanceState || 'unknown'}`}
                      >
                        <td data-label={t('date')}><span className="payroll-lesson-date">{displayDate(lesson.date, language)} <span>{lesson.startTime}</span></span>
                          {schedule.moved && <small> ({t(`weekdays.${schedule.dayOfWeek}`)} {schedule.startTime} → {t(`weekdays.${schedule.actualDayOfWeek}`)} {lesson.startTime})</small>}
                          <small className="payroll-lesson-participants"><Participants lesson={lesson} t={t} /></small>
                        </td>
                        <td data-label={t('attendance')}>
                          <TeacherSettlementStatus compact tone={({ missing: 'danger', checked: 'success' })[lesson.attendanceState] || 'muted'}>
                            {t(`attendanceStates.${lesson.attendanceState || 'unknown'}`)}
                          </TeacherSettlementStatus>
                          {target && lesson.attendanceState === 'missing' && (
                          <Link
                            className="btn payroll-outline-button payroll-complete-attendance"
                            aria-label={t('checkAttendance')}
                            to={target}
                            onClick={event => onAttendanceClick(event, lesson)}
                          >{t('completeAttendance')}
                          </Link>
                          )}
                        </td>
                        <td data-label={t('source')} className="payroll-lesson-source">{t(`financialSources.${lesson.financialSource || 'unrecorded'}`)}</td>
                        <td data-label={t('amount')} className="payroll-lesson-amount"><strong>{amountLabel(lesson)}</strong>
                          {!lesson.payoutEligible && <small>{t('noPayout')}</small>}
                        </td>
                        <td className="payroll-lesson-action">{target && lesson.attendanceState !== 'missing' && (
                        <Link className="payroll-attendance-info" aria-label={t('showAttendance')} title={t('showAttendance')} to={target} onClick={event => onAttendanceClick(event, lesson)}>
                          <i className="far fa-eye" aria-hidden="true" />
                        </Link>
                        )}
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
TeacherSettlementLessonGroups.propTypes = { report: PropTypes.object.isRequired,
  expandedGroupKeys: PropTypes.array.isRequired,
  missingOnly: PropTypes.bool.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  attendanceLink: PropTypes.func.isRequired,
  onAttendanceClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired };
